import { AlertCircleIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer, PageHeader } from "@/components/layout";
import { cn } from "@/lib/utils";
import config from "../../config";

import { ComingSoonCard } from "./coming-soon-card";
import { CreditGauge } from "./credit-gauge";
import { CurrentPlanCard } from "./current-plan-card";
import { FeatureProgressCard } from "./feature-progress-card";
import {
  FEATURE_CATEGORIES,
  groupFeaturesByCategory,
  type FeatureUsage,
} from "./feature-categories";

interface RemainingResponse {
  total?: number;
  used?: number;
  remaining?: number;
  overspendUsed?: number;
  cycleEnd?: string;
}

interface SubscriptionResponse {
  planId?: string;
  planName?: string;
  tier?: string;
  priceEur?: number;
  unlockedFeatures?: string[];
  supportTier?: string;
  freeCreditsIncluded?: number;
  pendingPlanId?: string;
  cycleStartedAt?: string;
  cycleEndsAt?: string;
  totalCredits?: number;
  creditsUsed?: number;
}

interface UsageResponse {
  totalRunCount?: number;
  totalCreditsConsumed?: number;
  features?: FeatureUsage[];
}

interface CycleEntry {
  cycleId?: string;
  cycleStartedAt?: string;
  cycleEndedAt?: string;
  totalCredits?: number;
  creditsUsed?: number;
  isCurrent?: boolean;
}

function StatCard({
  title,
  value,
  subtitle,
  valueClassName,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  valueClassName?: string;
}) {
  return (
    <Card className="h-full">
      <CardContent>
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className={cn("mt-1 text-3xl font-semibold", valueClassName)}>
          {value}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Credits() {
  const { t } = useTranslation();

  const [remaining, setRemaining] = useState<RemainingResponse | null>(null);
  const [subscription, setSubscription] =
    useState<SubscriptionResponse | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [cycles, setCycles] = useState<CycleEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [remainingRes, subscriptionRes, usageRes, cyclesRes] =
        await Promise.all([
          axios.get(`${config.apiBaseUrl}/usage/remaining`),
          axios.get(`${config.apiBaseUrl}/usage/subscription`),
          axios.get(`${config.apiBaseUrl}/usage/usage`),
          axios.get(`${config.apiBaseUrl}/usage/cycles`),
        ]);
      setRemaining(remainingRes.data);
      setSubscription(subscriptionRes.data);
      setUsage(usageRes.data);
      setCycles(cyclesRes.data);
    } catch (err) {
      console.error("Failed to fetch credits data:", err);
      const e = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        e?.response?.data?.message || e?.message || String(t("credits.error"))
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-[120px] rounded-md" />
      ))}
      <Skeleton className="col-span-full h-10 rounded-md" />
    </div>
  );

  const renderCategorySections = () => {
    const features = usage?.features ?? [];
    const byCategory = groupFeaturesByCategory(features);

    return FEATURE_CATEGORIES.map((cat) => {
      const featuresInCat = byCategory[cat.id];
      const categoryTotal = featuresInCat.reduce(
        (sum, f) => sum + f.creditsConsumed,
        0
      );

      if (cat.comingSoon) {
        return (
          <div key={cat.id} className="space-y-2">
            <h3 className="text-base font-semibold">
              {t(`credits.category.${cat.id}`)}
            </h3>
            <ComingSoonCard categoryId={cat.id} variant="category" />
          </div>
        );
      }

      return (
        <div key={cat.id} className="space-y-2">
          <h3 className="text-base font-semibold">
            {t(`credits.category.${cat.id}`)}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {featuresInCat.map((feat) =>
              feat.comingSoon ? (
                <ComingSoonCard
                  key={feat.featureId}
                  featureId={feat.featureId}
                />
              ) : (
                <FeatureProgressCard
                  key={feat.featureId}
                  featureId={feat.featureId}
                  runCount={feat.runCount}
                  creditsConsumed={feat.creditsConsumed}
                  categoryTotal={categoryTotal}
                />
              )
            )}
          </div>
        </div>
      );
    });
  };

  const renderDashboard = () => {
    if (!remaining) return null;
    const {
      total: creditsTotal = 0,
      used: creditsUsed = 0,
      remaining: creditsRemaining = 0,
      overspendUsed: overspend = 0,
      cycleEnd,
    } = remaining;
    const isOverspending = overspend > 0;

    return (
      <div className="space-y-6">
        {subscription && <CurrentPlanCard subscription={subscription} />}

        <Card>
          <CardContent className="space-y-3">
            <h3 className="text-lg font-semibold">
              {t("credits.gaugeTitle")}
            </h3>
            <CreditGauge
              used={creditsUsed}
              total={creditsTotal}
              overspend={overspend}
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <StatCard
            title={t("credits.totalCredits")}
            value={creditsTotal.toLocaleString()}
          />
          <StatCard
            title={t("credits.usedCredits")}
            value={creditsUsed.toLocaleString()}
            valueClassName={isOverspending ? "text-destructive" : undefined}
          />
          <StatCard
            title={t("credits.remainingCredits")}
            value={creditsRemaining.toLocaleString()}
            valueClassName={
              creditsRemaining <= 0
                ? "text-destructive"
                : "text-emerald-600"
            }
          />
          <StatCard
            title={t("credits.cycleEnd")}
            value={formatDate(cycleEnd)}
          />
        </div>

        {isOverspending && (
          <Alert variant="warning">
            <AlertCircleIcon />
            <AlertDescription>
              {t("credits.overspendWarning", {
                amount: overspend.toLocaleString(),
              })}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          <h2 className="text-lg font-semibold">
            {t("credits.byCategoryTitle")}
          </h2>
          {renderCategorySections()}
        </div>
      </div>
    );
  };

  const renderUsage = () => {
    if (!usage) return null;
    const { totalRunCount, totalCreditsConsumed, features } = usage;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StatCard
            title={t("credits.totalRuns")}
            value={(totalRunCount ?? 0).toLocaleString()}
          />
          <StatCard
            title={t("credits.totalConsumed")}
            value={(totalCreditsConsumed ?? 0).toLocaleString()}
          />
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("credits.feature")}</TableHead>
                <TableHead className="text-right">{t("credits.runs")}</TableHead>
                <TableHead className="text-right">
                  {t("credits.creditsConsumed")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features && features.length > 0 ? (
                features.map((feat) => (
                  <TableRow key={feat.featureId}>
                    <TableCell>
                      {t(`credits.features.${feat.featureId}`, {
                        defaultValue: feat.featureId,
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      {(feat.runCount ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(feat.creditsConsumed ?? 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    {t("credits.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    );
  };

  const renderCycles = () => {
    const history = Array.isArray(cycles) ? cycles : [];
    const currentCycle: CycleEntry | null =
      subscription?.cycleStartedAt
        ? {
            cycleId: "current",
            cycleStartedAt: subscription.cycleStartedAt,
            cycleEndedAt: subscription.cycleEndsAt,
            totalCredits: subscription.totalCredits,
            creditsUsed: subscription.creditsUsed,
            isCurrent: true,
          }
        : null;
    const cycleList = currentCycle ? [currentCycle, ...history] : history;

    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("credits.cycleStart")}</TableHead>
              <TableHead>{t("credits.cycleEnd")}</TableHead>
              <TableHead className="text-right">
                {t("credits.totalCredits")}
              </TableHead>
              <TableHead className="text-right">
                {t("credits.usedCredits")}
              </TableHead>
              <TableHead className="text-center">
                {t("credits.status")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycleList.length > 0 ? (
              cycleList.map((cycle, idx) => {
                const isActive = !!cycle.isCurrent;
                return (
                  <TableRow key={cycle.cycleId || idx}>
                    <TableCell>{formatDate(cycle.cycleStartedAt)}</TableCell>
                    <TableCell>{formatDate(cycle.cycleEndedAt)}</TableCell>
                    <TableCell className="text-right">
                      {(cycle.totalCredits ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(cycle.creditsUsed ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={isActive ? "default" : "outline"}>
                        {isActive
                          ? t("credits.active")
                          : t("credits.completed")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-6 text-center text-sm text-muted-foreground"
                >
                  {t("credits.noData")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    );
  };

  return (
    <PageContainer title={t("credits.pageTitle")} className="max-w-screen-2xl">
      <PageHeader title={t("credits.pageTitle")} />

      {error && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircleIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mt-6">
        {loading ? (
          renderSkeleton()
        ) : (
          <Tabs defaultValue="dashboard">
            <TabsList>
              <TabsTrigger value="dashboard">
                {t("credits.tabDashboard")}
              </TabsTrigger>
              <TabsTrigger value="usage">{t("credits.tabUsage")}</TabsTrigger>
              <TabsTrigger value="cycles">
                {t("credits.tabCycles")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="dashboard" className="mt-6">
              {renderDashboard()}
            </TabsContent>
            <TabsContent value="usage" className="mt-6">
              {renderUsage()}
            </TabsContent>
            <TabsContent value="cycles" className="mt-6">
              {renderCycles()}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageContainer>
  );
}
